import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    Divider,
    Paper,
    CircularProgress,
    FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import CustomYearDatePicker from '../CustomDates/CustomYearDatePicker/CustomYearDatePicker.js';
import CustomMonthDatePicker from '../CustomDates/CustomMonthDatePicker/CustomMonthDatePicker.js';
import AssetService from '../../../Services/assets.service';
import DocumentService from '../../../Services/documents.service';
import EnumsService from '../../../Services/enums.service';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { useHamburgerMenu } from '../../../Contexts/HamburgerMenuContext';
import CreateDepreciationJournalEntryDialog from './CreateDepreciationJournalEntryDialog';
import CreateDepreciationSortCodeDialog from './CreateDepreciationSortCodeDialog';
import Constants from '../../../assests/Constants/constants';
import Swal from 'sweetalert2';

dayjs.extend(utc);

const XTRAM_BTN_BASE_SX = {
    minHeight: 'var(--xtram-control-height, 40px)',
    px: 2,
    borderRadius: '10px',
    textTransform: 'none',
    fontFamily: 'var(--xtram-font)',
    fontWeight: 600,
    fontSize: 'var(--xtram-text-sm)',
    boxShadow: 'none',
    '&.Mui-disabled': { opacity: 0.55 },
};

const XTRAM_PRIMARY_BTN_SX = {
    ...XTRAM_BTN_BASE_SX,
    backgroundColor: 'var(--xtram-orange)',
    color: '#fff',
    '&:hover': { backgroundColor: 'var(--xtram-orange-hover)' },
    '&.Mui-disabled': { opacity: 0.55, color: '#fff', backgroundColor: 'var(--xtram-orange)' },
};

const XTRAM_OUTLINE_BTN_SX = {
    ...XTRAM_BTN_BASE_SX,
    border: '1px solid var(--xtram-blue)',
    color: 'var(--xtram-blue)',
    '&:hover': { backgroundColor: 'rgba(48, 79, 255, 0.08)' },
};

const XTRAM_GHOST_BTN_SX = {
    ...XTRAM_BTN_BASE_SX,
    minHeight: 'var(--xtram-control-height-sm, 32px)',
    border: '1px solid #e5e7f6',
    backgroundColor: '#f5f6fd',
    color: 'var(--xtram-blue)',
    '&:hover': { borderColor: 'var(--xtram-blue)', backgroundColor: '#eaedff' },
};

const XTRAM_PAPER_SX = {
    position: 'relative',
    width: '95vw',
    maxWidth: '1400px',
    borderRadius: '16px',
    border: '1px solid var(--xtram-blue)',
    fontFamily: 'var(--xtram-font)',
    boxShadow: '0 24px 64px rgba(51, 102, 255, 0.18), 0 4px 18px rgba(26, 31, 73, 0.1)',
    '&::before': {
        content: '""',
        position: 'absolute',
        insetBlock: 0,
        insetInlineStart: 0,
        width: '10px',
        background: 'var(--xtram-orange)',
        pointerEvents: 'none',
        zIndex: 2,
    },
    '& .MuiDialogContent-root, & .MuiDialogTitle-root, & .MuiDialogActions-root': {
        paddingInlineStart: '34px',
    },
};

const XTRAM_TITLE_SX = {
    background: '#fff',
    borderBottom: '1px solid #e5e7f6',
};

const DepreciationJournalEntriesModal = ({
    open,
    onClose,
    onConfirm,
    client,
    token,
    selectedEntries = [],
    title = 'UpdateDepreciationJournalEntries',
    direction = 'rtl',
    debitAccountOptions = [],
    sortCodeIdOptions = [],
    onJournalEntryCreated // Callback to refresh parent component (e.g., Profit and Loss Report)
}) => {
    const { t } = useTranslation();
    const { handleDocumentRowClick, editModalUpdated, setEditModalUpdated, isJustEditModalOpen, refreshEnumsCallback } = useHamburgerMenu();
    const [searchQuery, setSearchQuery] = useState('');
    const [assetGroupSearchQuery, setAssetGroupSearchQuery] = useState('');
    const [localSelectedEntries, setLocalSelectedEntries] = useState([]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [reportDate, setReportDate] = useState(() => {
        const storedEndDate = localStorage.getItem('EndDate');
        return storedEndDate ? dayjs.utc(storedEndDate) : dayjs.utc();
    });
    const [useRange, setUseRange] = useState(false);
    const [rangeStartMonth, setRangeStartMonth] = useState(() => {
        // Default start = January of the current year
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    });
    const [rangeEndMonth, setRangeEndMonth] = useState(() => new Date());
    // The committed start/end-of-month dates (applied via the Refresh button).
    // While useRange is true and these are null, we keep the last fetched data and wait for the user.
    const [appliedStartOfMonth, setAppliedStartOfMonth] = useState(null);
    const [appliedEndOfMonth, setAppliedEndOfMonth] = useState(null);
    const [depreciationData, setDepreciationData] = useState(null);
    const [journalEntries, setJournalEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [localDebitAccountOptions, setLocalDebitAccountOptions] = useState(debitAccountOptions);
    const [localSortCodeIdOptions, setLocalSortCodeIdOptions] = useState(sortCodeIdOptions);
    const [isCreateSortCodeDialogOpen, setIsCreateSortCodeDialogOpen] = useState(false);
    const [selectedAssetGroupForSortCode, setSelectedAssetGroupForSortCode] = useState(null);
    const [pendingSortCodeId, setPendingSortCodeId] = useState(null); // Store sortCodeId to set after sort code is created
    const [selectedAssetGroupAmount, setSelectedAssetGroupAmount] = useState(null); // Store the specific asset group's amount
    const [selectedAssetGroupForJournalEntry, setSelectedAssetGroupForJournalEntry] = useState(null); // Store asset group for pre-filling debit/credit accounts
    const [selectedAssetGroups, setSelectedAssetGroups] = useState([]); // Multi-select for asset groups

    useEffect(() => {
        setLocalSelectedEntries(selectedEntries);
    }, [selectedEntries, open]);

    // Update local options when props change
    useEffect(() => {
        setLocalDebitAccountOptions(debitAccountOptions);
        setLocalSortCodeIdOptions(sortCodeIdOptions);
    }, [debitAccountOptions, sortCodeIdOptions]);

    // Fetch options if not provided
    useEffect(() => {
        if (!open || !client || !token) return;

        const fetchOptions = async () => {
            // Only fetch if options are empty
            if (localDebitAccountOptions.length === 0 || localSortCodeIdOptions.length === 0) {
                try {
                    const [codeIdResponse, accountsResponse] = await Promise.all([
                        EnumsService.GetSortCodesByAccountantId(client.accountantId, token),
                        EnumsService.GetAccountCardsByCaseId(client.caseId, token)
                    ]);

                    if (codeIdResponse.ok && localSortCodeIdOptions.length === 0) {
                        const codeIdData = await codeIdResponse.json();
                        setLocalSortCodeIdOptions(codeIdData);
                    }

                    if (accountsResponse.ok && localDebitAccountOptions.length === 0) {
                        const accountsData = await accountsResponse.json();
                        setLocalDebitAccountOptions(accountsData);
                    }
                } catch (error) {
                    console.error('Error fetching options:', error);
                }
            }
        };

        fetchOptions();
    }, [open, client, token, localDebitAccountOptions.length, localSortCodeIdOptions.length]);

    // Extract fetchJournalEntries to be reusable
    const fetchJournalEntries = useCallback(async () => {
        if (!client || !token) return;

        try {
            // Determine the date window for the fetch.
            // - In range mode (StartMonth..EndMonth): start = appliedStartOfMonth, end = appliedEndOfMonth.
            // - Otherwise: start = start of year of reportDate, end = end of year of reportDate.
            const isRangeMode = useRange && !!appliedStartOfMonth && !!appliedEndOfMonth;
            const referenceDate = isRangeMode ? dayjs.utc(appliedEndOfMonth) : dayjs.utc(reportDate);
            const selectedYear = referenceDate.year();
            const startDate = isRangeMode
                ? dayjs.utc(appliedStartOfMonth).startOf('day').toDate()
                : referenceDate.startOf('year').toDate();
            const endDate = isRangeMode
                ? dayjs.utc(appliedEndOfMonth).endOf('day').toDate()
                : referenceDate.endOf('year').toDate();
            const endDateMs = endDate.getTime();
            const startDateMs = startDate.getTime();

            const response = await DocumentService.GetDocumentbyDateRange(
                'JournalEntries',
                client.caseId,
                startDate.toISOString(),
                endDate.toISOString(),
                token
            );

            if (response.ok) {
                const data = await response.json();
                const result = Array.isArray(data.result) ? data.result : (Array.isArray(data) ? data : []);

                // Filter entries with JournalEntryType = 1 (IsDepreciationJournal) within the selected window
                const depreciationEntries = result.filter(entry => {
                    // Check JournalEntryType - must be 1 (IsDepreciationJournal)
                    const entryType = entry.journalEntryType ?? entry.JournalEntryType ?? 0;

                    // If journalEntryType is not set (0), fallback to checking sortCodeId === '905'
                    const isDepreciationEntry = entryType === 1 || entry.sortCodeId === '905';

                    if (!isDepreciationEntry) {
                        return false;
                    }

                    // Frontend safety filter (UTC to avoid timezone issues)
                    const entryDate = entry.reportingMonthDate || entry.documentDate || entry.registrationMonthDate;
                    if (!entryDate) {
                        // If no date field, skip this entry to be safe
                        console.warn('Entry missing date field:', entry.id);
                        return false;
                    }

                    if (isRangeMode) {
                        const entryMs = dayjs.utc(entryDate).valueOf();
                        if (entryMs < startDateMs || entryMs > endDateMs) {
                            return false;
                        }
                    } else {
                        const entryYear = dayjs.utc(entryDate).year();
                        if (entryYear !== selectedYear) {
                            return false;
                        }
                    }

                    return true;
                });

                setJournalEntries(depreciationEntries);
            } else {
                setJournalEntries([]);
            }
        } catch (error) {
            console.error('Error fetching journal entries:', error);
            setJournalEntries([]);
        }
    }, [reportDate, useRange, appliedStartOfMonth, appliedEndOfMonth, client, token]);

    // Combined fetch function for both depreciation data and journal entries
    const fetchAllData = useCallback(async () => {
        if (!open || !client || !token) return;

        // In range mode wait until the user applies the date range via the Refresh button
        if (useRange && (!appliedStartOfMonth || !appliedEndOfMonth)) return;

        // Clear previous data immediately when the active period changes
        setJournalEntries([]);
        setDepreciationData(null);
        setLoading(true);

        const fetchDepreciationData = async () => {
            try {
                let response;
                if (useRange && appliedEndOfMonth) {
                    // Depreciation cutoff is determined by the end date.
                    // The backend uses EndDate as the report cutoff (start is implicit per asset activation).
                    response = await AssetService.GetDepreciationDetails(
                        client.caseId,
                        null,
                        token,
                        appliedEndOfMonth
                    );
                } else {
                    // Convert UTC dayjs to Date for the service call
                    const dateForService = dayjs.isDayjs(reportDate) ? dayjs.utc(reportDate).toDate() : reportDate;
                    response = await AssetService.GetDepreciationDetails(client.caseId, dateForService, token);
                }
                if (response && response.assetGroups) {
                    setDepreciationData(response);
                } else {
                    setDepreciationData(null);
                }
            } catch (error) {
                console.error('Error fetching depreciation data:', error);
                setDepreciationData(null);
            }
        };

        // Fetch both in parallel and set loading to false when both complete
        await Promise.all([fetchDepreciationData(), fetchJournalEntries()]);
        setLoading(false);
    }, [open, reportDate, useRange, appliedStartOfMonth, appliedEndOfMonth, client, token, fetchJournalEntries]);

    // Set the ref callback for refreshing data
    useEffect(() => {
        refreshJournalEntriesRef.current = fetchAllData;
    }, [fetchAllData]);

    // Fetch depreciation data and journal entries when modal opens or date changes
    useEffect(() => {
        if (!open || !client || !token) return;
        fetchAllData();
    }, [open, reportDate, useRange, appliedStartOfMonth, appliedEndOfMonth, client, token, fetchAllData]);

    // Track previous state of isJustEditModalOpen to detect when EditModal closes
    const prevIsJustEditModalOpenRef = useRef(isJustEditModalOpen);


    // Listen for EditModal updates and refresh journal entries
    useEffect(() => {
        if (editModalUpdated && open && client && token) {
            // Refetch all data when EditModal updates a document
            if (refreshJournalEntriesRef.current) {
                refreshJournalEntriesRef.current();
            }
            // Reset the flag
            if (setEditModalUpdated) {
                setEditModalUpdated(false);
            }
        }
    }, [editModalUpdated, open, client, token, setEditModalUpdated]);

    // Also listen for when EditModal closes (fallback if editModalUpdated is not set)
    useEffect(() => {
        // If EditModal was open and now it's closed, and our modal is still open, refetch
        if (prevIsJustEditModalOpenRef.current === true && isJustEditModalOpen === false && open && client && token) {
            // EditModal just closed - refetch journal entries to get updated data
            fetchJournalEntries();
        }
        // Update the ref for next time
        prevIsJustEditModalOpenRef.current = isJustEditModalOpen;
    }, [isJustEditModalOpen, open, client, token, fetchJournalEntries]);

    const handleYearChange = (newDate) => {
        // CustomYearDatePicker returns UTC Date, so use UTC to avoid timezone issues
        const updated = dayjs.utc(newDate);
        setReportDate(updated);
    };

    // Calculate sumDepreciationThisYear for each asset group
    const getAssetGroupDepreciation = () => {
        if (!depreciationData || !depreciationData.assetGroups) return [];
        
        return depreciationData.assetGroups.map(group => {
            let sumDepreciationThisYear = 0;
            
            if (group.assets && group.assets.length > 0) {
                group.assets.forEach(asset => {
                    sumDepreciationThisYear += asset.depreciationThisYear || 0;
                });
            }
            
            return {
                assetGroupId: group.assetGroupId,
                assetGroupName: group.assetGroupName === "Unassigned Assets" 
                    ? t('UnassignedAssets') 
                    : group.assetGroupName,
                sumDepreciationThisYear
            };
        });
    };

    const assetGroupsWithDepreciation = getAssetGroupDepreciation();
    
    // Helper function to calculate depreciation difference for a specific asset group
    const getAssetGroupDepreciationDifference = (group) => {
        // Round sumDepreciationThisYear to nearest integer first
        const roundedSumDepreciation = Math.round(group.sumDepreciationThisYear || 0);
        
        // Find the sort code for this asset group
        const expectedSortCodeName = `${Constants.DepreciationPrefixes.SortCode}${group.assetGroupName}`;
        const sortCode = localSortCodeIdOptions.find(
            sc => sc.sortCodeName === expectedSortCodeName
        );
        
        if (!sortCode) {
            // No sort code exists for this group yet, so no journal entries
            return roundedSumDepreciation;
        }
        
        // Find all journal entries with this sortCodeId
        const groupJournalEntries = journalEntries.filter(
            entry => String(entry.sortCodeId) === String(sortCode.sortCodeId)
        );
        
        // Sum the amounts of journal entries for this group
        const sumJournalEntryAmounts = groupJournalEntries.reduce(
            (sum, entry) => sum + (entry.amount || 0),
            0
        );
        
        // Calculate difference: rounded total depreciation - sum of journal entry amounts
        return roundedSumDepreciation - sumJournalEntryAmounts;
    };
    
    // Helper function to check if a group can be selected via checkbox (multi-select)
    // This is used for "Select All" - excludes "5 - רכבים פרטיים"
    const canSelectGroup = (group) => {
        // Check if amount is greater than 0
        const depreciationDiff = getAssetGroupDepreciationDifference(group);
        if (depreciationDiff <= 0) return false;
        
        // Check if it's "5 - רכבים פרטיים" - this group cannot be selected via "Select All"
        const groupDisplayName = group.assetGroupId ? `${group.assetGroupId} - ${group.assetGroupName}` : group.assetGroupName;
        if (groupDisplayName === '5 - רכבים פרטיים') return false;
        
        return true;
    };
    
    // Helper function to check if checkbox should be disabled
    const isCheckboxDisabled = (group) => {
        // Check if amount is greater than 0
        const depreciationDiff = getAssetGroupDepreciationDifference(group);
        if (depreciationDiff <= 0) return true;
        
        // "5 - רכבים פרטיים" checkbox should be disabled if multiple groups are already selected
        const isPrivateCars = isPrivateCarsGroup(group);
        if (isPrivateCars && selectedAssetGroups.length > 1) {
            return true; // Disable when multiple groups are selected
        }
        
        // "5 - רכבים פרטיים" checkbox is enabled for individual selection (when 0 or 1 groups selected)
        return false;
    };
    
    // Helper function to check if a group is "5 - רכבים פרטיים"
    const isPrivateCarsGroup = (group) => {
        const groupDisplayName = group.assetGroupId ? `${group.assetGroupId} - ${group.assetGroupName}` : group.assetGroupName;
        return groupDisplayName === '5 - רכבים פרטיים';
    };
    
    // Filter asset groups by search query (show all groups, but disable checkboxes for non-selectable ones)
    const filteredAssetGroups = assetGroupsWithDepreciation.filter(group => {
        // Apply search filter only
        if (!assetGroupSearchQuery) return true;
        const searchLower = assetGroupSearchQuery.toLowerCase();
        const groupName = group.assetGroupName?.toLowerCase() || '';
        const groupId = group.assetGroupId?.toString().toLowerCase() || '';
        return groupName.includes(searchLower) || groupId.includes(searchLower);
    });
    
    // Handler for asset group checkbox toggle
    const handleAssetGroupToggle = (group) => {
        // Prevent selection if checkbox is disabled (amount <= 0)
        const depreciationDiff = getAssetGroupDepreciationDifference(group);
        if (depreciationDiff <= 0) return;
        
        // Check if trying to select "5 - רכבים פרטיים" when multiple groups are already selected
        const isPrivateCars = isPrivateCarsGroup(group);
        if (isPrivateCars && selectedAssetGroups.length > 1) {
            // Cannot select "5 - רכבים פרטיים" when multiple groups are selected
            return;
        }
        
        // Check if trying to select another group when "5 - רכבים פרטיים" is already selected
        if (!isPrivateCars && selectedAssetGroups.length === 1) {
            const existingGroup = selectedAssetGroups[0];
            if (isPrivateCarsGroup(existingGroup)) {
                // Cannot select other groups when "5 - רכבים פרטיים" is selected
                return;
            }
        }
        
        // Allow selection/deselection
        setSelectedAssetGroups(prev => {
            const isSelected = prev.some(selected => 
                selected.assetGroupId === group.assetGroupId && 
                selected.assetGroupName === group.assetGroupName
            );
            if (isSelected) {
                return prev.filter(selected => 
                    !(selected.assetGroupId === group.assetGroupId && 
                      selected.assetGroupName === group.assetGroupName)
                );
            } else {
                return [...prev, group];
            }
        });
    };
    
    // Handler for "Select All" checkbox
    const handleSelectAllAssetGroups = () => {
        // Get all selectable groups
        const selectableGroups = filteredAssetGroups.filter(group => canSelectGroup(group));
        
        // Check if all selectable groups are already selected
        const allSelected = selectableGroups.every(group => 
            selectedAssetGroups.some(selected => 
                selected.assetGroupId === group.assetGroupId && 
                selected.assetGroupName === group.assetGroupName
            )
        );
        
        if (allSelected) {
            // Deselect all selectable groups
            setSelectedAssetGroups(prev => 
                prev.filter(selected => 
                    !selectableGroups.some(group => 
                        group.assetGroupId === selected.assetGroupId && 
                        group.assetGroupName === selected.assetGroupName
                    )
                )
            );
        } else {
            // Select all selectable groups
            setSelectedAssetGroups(prev => {
                const newSelections = selectableGroups.filter(group => 
                    !prev.some(selected => 
                        selected.assetGroupId === group.assetGroupId && 
                        selected.assetGroupName === group.assetGroupName
                    )
                );
                return [...prev, ...newSelections];
            });
        }
    };
    
    // Check if all selectable groups are selected
    const allSelectableGroupsSelected = useMemo(() => {
        const selectableGroups = filteredAssetGroups.filter(group => canSelectGroup(group));
        if (selectableGroups.length === 0) return false;
        return selectableGroups.every(group => 
            selectedAssetGroups.some(selected => 
                selected.assetGroupId === group.assetGroupId && 
                selected.assetGroupName === group.assetGroupName
            )
        );
    }, [filteredAssetGroups, selectedAssetGroups]);
    
    const totalDepreciationThisYear = Math.round(
        assetGroupsWithDepreciation.reduce(
            (sum, group) => sum + group.sumDepreciationThisYear, 
            0
        )
    );

    const filteredEntries = journalEntries
        .filter(entry =>
            entry.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            entry.id?.toString().includes(searchQuery) ||
            entry.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            entry.uploadNumber?.toString().includes(searchQuery) ||
            String(entry.sortCodeId || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            String(entry.debitAccount || '').toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => {
            // Sort by uploadNumber in ascending order
            const uploadA = a.uploadNumber !== undefined && a.uploadNumber !== null ? Number(a.uploadNumber) : -1;
            const uploadB = b.uploadNumber !== undefined && b.uploadNumber !== null ? Number(b.uploadNumber) : -1;
            return uploadA - uploadB;
        });

    // Calculate total amount of filtered entries
    const totalJournalEntriesAmount = filteredEntries.reduce((sum, entry) => {
        return sum + (entry.amount || 0);
    }, 0);

    // Calculate depreciation difference: totalDepreciationThisYear - totalJournalEntriesAmount
    const depreciationDifference = totalDepreciationThisYear - totalJournalEntriesAmount;

    const handleEntryToggle = (entry) => {
        setLocalSelectedEntries(prev => {
            const isSelected = prev.some(selected => selected.id === entry.id);
            if (isSelected) {
                return prev.filter(selected => selected.id !== entry.id);
            } else {
                return [...prev, entry];
            }
        });
    };

    const handleSelectAll = () => {
        setLocalSelectedEntries(filteredEntries);
    };

    const handleDeselectAll = () => {
        setLocalSelectedEntries([]);
    };

    const handleConfirm = () => {
        // If exactly one asset group is selected, use the same logic as clicking the button for that group
        if (selectedAssetGroups.length === 1) {
            const selectedGroup = selectedAssetGroups[0];
            // Use the same logic as clicking the button for that specific group
            handleCreateJournalEntryForAssetGroup(selectedGroup);
        } else if (selectedAssetGroups.length > 1) {
            // Multiple groups selected - calculate the sum of their depreciation differences
            const selectedGroupsDepreciationSum = selectedAssetGroups.reduce((sum, group) => {
                const groupDepreciationDiff = getAssetGroupDepreciationDifference(group);
                return sum + groupDepreciationDiff;
            }, 0);
            
            // Set the amount for the selected groups
            setSelectedAssetGroupAmount(Math.round(selectedGroupsDepreciationSum));
            
            // Pre-fill with specific values: sortCodeId='905', debitAccount='1300000668', creditAccount='103'
            setPendingSortCodeId('905');
            setIsCreateDialogOpen(true);
        } else {
            // No groups selected - open the create dialog normally
            setIsCreateDialogOpen(true);
        }
    };

    const handleCreateDialogClose = () => {
        setIsCreateDialogOpen(false);
    };

    // Use ref to store the refresh callback
    const refreshJournalEntriesRef = useRef(null);

    const handleCreateSaveSuccess = async () => {
        // Refresh journal entries after successful save
        if (refreshJournalEntriesRef.current) {
            await refreshJournalEntriesRef.current();
        }
        
        // Reset pending sort code ID
        setPendingSortCodeId(null);
        
        // Refresh enums in HamburgerMenu to update TransactionSearchResultModal
        if (refreshEnumsCallback) {
            refreshEnumsCallback();
        }
        
        // Notify parent component (e.g., Profit and Loss Report) to refresh
        if (onJournalEntryCreated) {
            onJournalEntryCreated();
        }
    };

    const handleCancel = () => {
        setLocalSelectedEntries(selectedEntries);
        setSelectedAssetGroups([]); // Reset selected asset groups
        onClose();
    };

    const handleUpdateSelectedEntry = () => {
        if (localSelectedEntries.length === 1) {
            const selectedEntry = localSelectedEntries[0];
            // Call handleDocumentRowClick with the entry's id and documentType
            // Journal entries use 'JournalEntries' as the documentType
            if (handleDocumentRowClick && selectedEntry.id) {
                handleDocumentRowClick(selectedEntry.id, 'JournalEntries');
                // onClose(); // Close the modal after opening EditModal
            }
        }
    };

    // Handle creating journal entry for a specific asset group
    const handleCreateJournalEntryForAssetGroup = async (assetGroup) => {
        const expectedSortCodeName = `${Constants.DepreciationPrefixes.SortCode}${assetGroup.assetGroupName}`;
        
        // Calculate the depreciation difference for this asset group (total depreciation - journal entries amount)
        const groupDepreciationDifference = getAssetGroupDepreciationDifference(assetGroup);
        const roundedDifference = Math.round(groupDepreciationDifference);
        
        // Check if the depreciation difference is 0 or negative
        // Exception: "5 - רכבים פרטיים" can still be clicked even with 0 amount
        const isPrivateCars = isPrivateCarsGroup(assetGroup);
        if (roundedDifference <= 0 && !isPrivateCars) {
            // Show SweetAlert2 modal with error message
            await Swal.fire({
                title: t('NoDepreciationForGroup') || 'לא קיים עבור קבוצה זו',
                text: t('NoDepreciationForGroupMessage') || `לא קיים פחת עבור קבוצה זו: ${assetGroup.assetGroupName}`,
                icon: 'warning',
                confirmButtonText: t('OK') || 'אישור',
                confirmButtonColor: '#304FFF',
            });
            return;
        }
        
        // Store the asset group's depreciation difference and the asset group itself
        // For "5 - רכבים פרטיים", use 0 if the difference is negative or 0
        setSelectedAssetGroupAmount(roundedDifference > 0 ? roundedDifference : 0);
        setSelectedAssetGroupForJournalEntry(assetGroup);
        
        // Check if sort code exists with this name
        const existingSortCode = localSortCodeIdOptions.find(
            sortCode => sortCode.sortCodeName === expectedSortCodeName
        );

        if (existingSortCode) {
            // Sort code exists - open journal entry dialog with this sort code selected
            setPendingSortCodeId(existingSortCode.sortCodeId);
            setIsCreateDialogOpen(true);
        } else {
            // Sort code doesn't exist - open sort code dialog first
            setSelectedAssetGroupForSortCode(assetGroup);
            setIsCreateSortCodeDialogOpen(true);
        }
    };

    // Handle sort code creation success
    const handleSortCodeSaveSuccess = async (newSortCodeId) => {
        // Refresh sort codes list
        if (client && client.accountantId && token) {
            try {
                const response = await EnumsService.GetSortCodesByAccountantId(client.accountantId, token);
                if (response.ok) {
                    const sortCodesData = await response.json();
                    setLocalSortCodeIdOptions(sortCodesData);
                    
                    // Recalculate the depreciation difference for the asset group
                    // This ensures we have the correct amount even if journal entries were created in the meantime
                    if (selectedAssetGroupForJournalEntry) {
                        const groupDepreciationDifference = getAssetGroupDepreciationDifference(selectedAssetGroupForJournalEntry);
                        setSelectedAssetGroupAmount(Math.round(groupDepreciationDifference));
                    }
                    
                    // Close sort code dialog
                    setIsCreateSortCodeDialogOpen(false);
                    
                    // Open journal entry dialog with the newly created sort code
                    if (newSortCodeId) {
                        setPendingSortCodeId(newSortCodeId);
                        setIsCreateDialogOpen(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching updated sort codes:', error);
            }
        }
    };

    return (
        <>
        <Dialog 
            open={open} 
            onClose={handleCancel}
            maxWidth="lg"
            fullWidth
            dir={direction}
            disableEnforceFocus
            disableRestoreFocus
            sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(20, 30, 43, 0.45)', backdropFilter: 'blur(6px)' } }}
            PaperProps={{
                sx: XTRAM_PAPER_SX,
            }}
        >
            <DialogTitle sx={XTRAM_TITLE_SX}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography sx={{ fontFamily: 'var(--xtram-font)', fontWeight: 700, fontSize: 'var(--xtram-text-xl)', color: 'var(--xtram-blue)' }}>
                        {t(title)}
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button size="small" onClick={handleSelectAll} sx={XTRAM_GHOST_BTN_SX}>
                            {t('SelectAll')}
                        </Button>
                        <Button size="small" onClick={handleDeselectAll} sx={XTRAM_GHOST_BTN_SX}>
                            {t('DeselectAll')}
                        </Button>
                    </Box>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {/* Search, Year Date Picker and Total Depreciation in same row */}
                <Box mt={1.5} mb={1} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    flexDirection: direction === 'rtl' ? 'row' : 'row-reverse',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            placeholder={t('SearchAssetGroups')}
                            value={assetGroupSearchQuery}
                            onChange={(e) => setAssetGroupSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                            sx={{ 
                                width: '350px',
                                '& .MuiOutlinedInput-root': {
                                    height: '40px'
                                }
                            }}
                        />
                        {!useRange && (
                            <Box sx={{ width: '190px' }} >
                                <CustomYearDatePicker
                                    date={reportDate}
                                    label={t("Year")}
                                    onDateChange={handleYearChange}
                                    height="40px"
                                />
                            </Box>
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useRange}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setUseRange(checked);
                                        if (checked) {
                                            if (!rangeStartMonth) {
                                                const now = new Date();
                                                setRangeStartMonth(new Date(Date.UTC(now.getUTCFullYear(), 0, 1)));
                                            }
                                            if (!rangeEndMonth) {
                                                setRangeEndMonth(new Date());
                                            }
                                        } else {
                                            // Toggling off range mode -> clear applied range so we fall back to year mode.
                                            setAppliedStartOfMonth(null);
                                            setAppliedEndOfMonth(null);
                                        }
                                    }}
                                    sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' }, p: 0.5 }}
                                    size="small"
                                />
                            }
                            label={<span style={{ fontSize: 12 }}>{t('DateRange') || t('UpToMonth') || 'עד חודש'}</span>}
                            sx={{ mx: 0 }}
                        />
                        {useRange && (
                            <>
                                <Box sx={{ width: '170px' }}>
                                    <CustomMonthDatePicker
                                        date={rangeStartMonth}
                                        label={t('StartMonth') || 'חודש התחלה'}
                                        onDateChange={(d) => setRangeStartMonth(d)}
                                        height="40px"
                                    />
                                </Box>
                                <Box sx={{ width: '170px' }}>
                                    <CustomMonthDatePicker
                                        date={rangeEndMonth}
                                        label={t('EndMonth') || 'חודש סיום'}
                                        onDateChange={(d) => setRangeEndMonth(d)}
                                        height="40px"
                                    />
                                </Box>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                        if (!rangeStartMonth || !rangeEndMonth) return;
                                        const startD = new Date(rangeStartMonth);
                                        const startOfMonth = new Date(Date.UTC(startD.getFullYear(), startD.getMonth(), 1));
                                        const endD = new Date(rangeEndMonth);
                                        const endOfMonth = new Date(Date.UTC(endD.getFullYear(), endD.getMonth() + 1, 0));
                                        if (startOfMonth.getTime() > endOfMonth.getTime()) {
                                            // Guard against invalid range; ignore.
                                            return;
                                        }
                                        // Sync reportDate to the end-month's year so toggling back to year mode is consistent.
                                        setReportDate(dayjs.utc(endOfMonth));
                                        setAppliedStartOfMonth(startOfMonth);
                                        setAppliedEndOfMonth(endOfMonth);
                                    }}
                                    disabled={!rangeStartMonth || !rangeEndMonth || loading}
                                    sx={{
                                        backgroundColor: '#304FFF',
                                        textTransform: 'none',
                                        fontSize: 12,
                                        borderRadius: '6px',
                                        py: 0.5,
                                        minHeight: 36,
                                        minWidth: 'auto',
                                        px: 1.5,
                                        '&:hover': { backgroundColor: '#2040DD' },
                                    }}
                                >
                                    {t('Refresh') || 'רענן'}
                                </Button>
                            </>
                        )}
                        {loading && (
                            <CircularProgress size={24} />
                        )}
                    </Box>
                    {depreciationData && assetGroupsWithDepreciation.length > 0 && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            padding: '8px 16px',
                            backgroundColor: '#f0f4ff',
                            borderRadius: '8px',
                            border: '1px solid #304FFF',
                            whiteSpace: 'nowrap'
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF' }}>
                                {t('TotalDepreciationThisYear')}:
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#304FFF' }}>
                                {beautifyNumber(totalDepreciationThisYear, 0)}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Asset Groups with Depreciation Totals */}
                {depreciationData && assetGroupsWithDepreciation.length > 0 && (
                    <Box mb={1}>
                        {/* Header Row */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                pb: 1,
                                mb: 1,
                                borderBottom: '2px solid #304FFF'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                <Checkbox
                                    checked={allSelectableGroupsSelected}
                                    onChange={handleSelectAllAssetGroups}
                                    indeterminate={
                                        selectedAssetGroups.length > 0 && 
                                        !allSelectableGroupsSelected &&
                                        filteredAssetGroups.some(group => 
                                            canSelectGroup(group) && 
                                            selectedAssetGroups.some(selected => 
                                                selected.assetGroupId === group.assetGroupId && 
                                                selected.assetGroupName === group.assetGroupName
                                            )
                                        )
                                    }
                                    size="small"
                                    sx={{
                                        paddingLeft: '15px',
                                        color: '#304FFF',
                                        '&.Mui-checked': {
                                            color: '#304FFF',
                                        },
                                    }}
                                />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF'}}>
                                    {t('DepreciationGroupsDistribution')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF', minWidth: '120px', maxWidth: '120px', textAlign: 'center',paddingRight: '10px' }}>
                                    {t('CreateDepreciationJournalEntry')}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF', minWidth: '80px', textAlign: 'center',paddingRight: '15px' }}>
                                    {t('DepreciationDifference')}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF', minWidth: '80px', textAlign: 'center',paddingRight: '20px' }}>
                                    {t('TotalDepreciation')}
                                </Typography>
                            </Box>
                        </Box>
                        <Paper 
                            elevation={1} 
                            sx={{ 
                                p: 1.5, 
                                backgroundColor: '#f8f9fa',
                                maxHeight: '150px',
                                overflowY: 'auto'
                            }}
                        >
                            {filteredAssetGroups.map((group) => {
                                const isSelected = selectedAssetGroups.some(selected => 
                                    selected.assetGroupId === group.assetGroupId && 
                                    selected.assetGroupName === group.assetGroupName
                                );
                                const isSelectable = canSelectGroup(group);
                                const checkboxDisabled = isCheckboxDisabled(group);
                                return (
                                    <Box 
                                        key={group.assetGroupId || 'unassigned'}
                                        sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 0.5,
                                            borderBottom: '1px solid #e0e0e0',
                                            '&:last-child': { borderBottom: 'none' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => handleAssetGroupToggle(group)}
                                                disabled={checkboxDisabled}
                                                size="small"
                                                sx={{
                                                    padding: '4px',
                                                    color: '#304FFF',
                                                    '&.Mui-checked': {
                                                        color: '#304FFF',
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: '#BDBDBD',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {group.assetGroupId ? `${group.assetGroupId} - ${group.assetGroupName}` : group.assetGroupName}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleCreateJournalEntryForAssetGroup(group)}
                                                sx={{
                                                    backgroundColor: '#304FFF',
                                                    color: 'white',
                                                    fontSize: '0.7rem',
                                                    padding: '2px 8px',
                                                    minWidth: '120px',
                                                    maxWidth: '120px',
                                                    height: '28px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    '&:hover': {
                                                        backgroundColor: '#1E40FF',
                                                    }
                                                }}
                                            >
                                                {t('CreateDepreciationJournalEntry')}
                                            </Button>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: 600,
                                                    color: 'rgb(2, 83, 102)',
                                                    backgroundColor: 'rgb(255, 243, 224)',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    minWidth: '80px',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                {beautifyNumber(getAssetGroupDepreciationDifference(group), 0)}
                                            </Typography>
                                            <Chip 
                                                label={beautifyNumber(group.sumDepreciationThisYear, 0)}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ 
                                                    fontWeight: 600,
                                                    minWidth: '80px',
                                                    justifyContent: 'center'
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Paper>
                    </Box>
                )}

                <Divider sx={{ my: 1.4, borderBottom: '2px solid #304FFF' }} />

                {/* Journal Entries Section */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#304FFF' }}>
                    {t('DepreciationJournalEntriesList')}
                </Typography>

                <Box mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: direction === 'ltr' ? 'row-reverse' : 'row', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: direction === 'ltr' ? 'row-reverse' : 'row' }}>
                        <TextField
                            placeholder={t('SearchDepreciationJournalEntry')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                            sx={{ width: '350px' }}
                        />
                        <Chip
                            label={`${t('Selected')}: ${localSelectedEntries.length} / ${filteredEntries.length}`}
                            size="small"
                            sx={{
                                backgroundColor: '#f0f4ff',
                                color: '#304FFF',
                                fontWeight: 500,
                                border: '1px solid #304FFF'
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: direction === 'ltr' ? 'row-reverse' : 'row' }}>
                        {depreciationData && assetGroupsWithDepreciation.length > 0 && (
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                padding: '8px 16px',
                                backgroundColor: '#fff4e6',
                                borderRadius: '8px',
                                border: '1px solid #FF9800',
                                whiteSpace: 'nowrap'
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgb(2, 83, 102)' }}>
                                    {t('DepreciationDifference')}:
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgb(2, 83, 102)' }}>
                                    {beautifyNumber(depreciationDifference, 0)}
                                </Typography>
                            </Box>
                        )}
                        {filteredEntries.length > 0 && (
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                padding: '8px 16px',
                                backgroundColor: '#f0f4ff',
                                borderRadius: '8px',
                                border: '1px solid #304FFF',
                                whiteSpace: 'nowrap'
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#304FFF' }}>
                                    {t('TotalJournalEntries')}:
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF' }}>
                                    {beautifyNumber(totalJournalEntriesAmount, 2)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Header Row */}
                {filteredEntries.length > 0 && (
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            justifyContent: 'center',
                            alignItems: 'center',
                            pb: 0.8,
                            mb: 1,
                            borderBottom: '2px solid #304FFF',
                            px: 1,
                            flexDirection: direction === 'rtl' ? 'row-reverse' : 'row'
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF', flex: 1, textAlign:'center' }}>
                            {t('Amount')}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF', flex: 1, textAlign: 'center' }}>
                            {t('Credit Account')}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF', flex: 1, textAlign: 'center' }}>
                            {t('Debit Account')}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF', flex: 1, textAlign: 'center' }}>
                            {t('Code ID')}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#304FFF', flex: 1, textAlign:'center' }}>
                            {t('UploadNumber')}
                        </Typography>
                        <Box sx={{ width: '16px' }} /> {/* Compact space for checkbox */}
                    </Box>
                )}

                <Box 
                    sx={{ 
                        maxHeight: 200, 
                        overflowY: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 0,
                        backgroundColor: '#fafafa'
                    }}
                >
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : filteredEntries.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                            {t('NoEntriesFound')}
                        </Typography>
                    ) : (
                        filteredEntries.map((entry) => {
                            const isSelected = localSelectedEntries.some(selected => selected.id === entry.id);
                            
                            // Get Credit Account option - match JournalEntryDataGrid.jsx (line 564-579) exactly
                            const creditAccountOption = localDebitAccountOptions.find(
                                (option) => String(option.cardId) === String(entry.creditAccount)
                            );
                            const creditAccountDisplay = creditAccountOption 
                                ? `${creditAccountOption.cardId} - ${creditAccountOption.name}` 
                                : '';
                            
                            // Get Debit Account option - match JournalEntryDataGrid.jsx (line 589-594) exactly
                            const debitAccountOption = localDebitAccountOptions.find(
                                (option) => String(option.cardId) === String(entry.debitAccount)
                            );
                            const debitAccountDisplay = debitAccountOption 
                                ? `${debitAccountOption.cardId} - ${debitAccountOption.name}` 
                                : '';
                            
                            // Get Sort Code option - match JournalEntryDataGrid.jsx (line 603-614) exactly
                            const sortCodeValue = entry.sortCodeId || '';
                            const sortCodeOption = localSortCodeIdOptions.find(
                                (option) => String(option.sortCodeId) === String(sortCodeValue)
                            );
                            const sortCodeName = sortCodeOption ? sortCodeOption.sortCodeName : '';
                            const sortCodeDisplay = sortCodeValue 
                                ? `${sortCodeValue}${sortCodeName ? ` - ${sortCodeName}` : ''}` 
                                : '';
                            
                            return (
                                <Box
                                    key={entry.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        py: 1,
                                        px: 1,
                                        mb: 0.5,
                                        borderRadius: '4px',
                                        backgroundColor: isSelected ? 'rgba(48, 79, 255, 0.05)' : 'transparent',
                                        border: isSelected ? '1px solid #304FFF' : '1px solid transparent',
                                        transition: 'all 0.2s',
                                        gap: 1,
                                        flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
                                        '&:hover': {
                                            backgroundColor: 'rgba(48, 79, 255, 0.02)'
                                        }
                                    }}
                                >

                                    <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 600, color: '#304FFF' }}>
                                        {entry.amount ? beautifyNumber(entry.amount, 2) : '0.00'} 
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 500 }}>
                                        {creditAccountDisplay}
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 500 }}>
                                        {debitAccountDisplay}
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 500 }}>
                                        {sortCodeDisplay}
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 500 }}>
                                        {entry.uploadNumber !== undefined ? entry.uploadNumber : 'N/A'}
                                    </Typography>
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={() => handleEntryToggle(entry)}
                                        size="small"
                                        sx={{
                                            padding: 0,
                                            '&.Mui-checked': {
                                                color: '#304FFF'
                                            }
                                        }}
                                    />
                                </Box>
                            );
                        })
                    )}
                </Box>

                {localSelectedEntries.length > 0 && (
                    <Box mt={1}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('Selected')}:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.4}>
                            {localSelectedEntries.map((entry) => {
                                // Get Debit Account option - same logic as in the main list
                                const debitAccountOption = localDebitAccountOptions.find(
                                    (option) => option.cardId === entry.debitAccount
                                );
                                const debitAccountDisplay = debitAccountOption 
                                    ? `${debitAccountOption.cardId} - ${debitAccountOption.name}` 
                                    : (entry.debitAccount || '-');
                                
                                return (
                                    <Chip
                                        key={entry.id}
                                        label={`(${entry.uploadNumber !== undefined ? entry.uploadNumber : 'N/A'}) ${debitAccountDisplay}`}
                                        size="small"
                                        onDelete={() => handleEntryToggle(entry)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions sx={{ px: 3, py: 1.75, gap: 1, borderTop: '1px solid #e5e7f6' }}>
                <Button onClick={handleCancel} sx={XTRAM_OUTLINE_BTN_SX}>
                    {t('Cancel')}
                </Button>
                <Button
                    onClick={handleUpdateSelectedEntry}
                    disabled={localSelectedEntries.length === 0 || localSelectedEntries.length > 1 || loading}
                    sx={XTRAM_OUTLINE_BTN_SX}
                >
                    {t('UpdateDepreciationJournalEntry')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={loading || selectedAssetGroups.length === 0}
                    sx={XTRAM_PRIMARY_BTN_SX}
                >
                    {t('CreateDepreciationJournalEntry')} {selectedAssetGroups.length > 0 && `(${selectedAssetGroups.length})`}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Create Depreciation Journal Entry Dialog */}
        <CreateDepreciationJournalEntryDialog
            open={isCreateDialogOpen}
            onClose={() => {
                handleCreateDialogClose();
                setPendingSortCodeId(null); // Reset when closing
                setSelectedAssetGroupAmount(null); // Reset when closing
                setSelectedAssetGroupForJournalEntry(null); // Reset when closing
                setSelectedAssetGroups([]); // Reset selected asset groups
            }}
            client={client}
            token={token}
            direction={direction}
            sortCodeIdOptions={localSortCodeIdOptions}
            debitAccountOptions={localDebitAccountOptions}
            reportDate={reportDate}
            depreciationDifference={selectedAssetGroupAmount !== null ? selectedAssetGroupAmount : depreciationDifference}
            loading={loading}
            setLoading={setLoading}
            onSaveSuccess={handleCreateSaveSuccess}
            assetGroups={depreciationData?.assetGroups || []}
            initialSortCodeId={pendingSortCodeId || (selectedAssetGroups.length > 0 ? '905' : null)}
            initialAssetGroup={selectedAssetGroupForJournalEntry}
            journalEntries={journalEntries}
            parentSortCodeIdOptions={localSortCodeIdOptions}
            selectedAssetGroups={selectedAssetGroups} // Pass selected asset groups
            getAssetGroupDepreciationDifference={getAssetGroupDepreciationDifference} // Pass the helper function
        />

        {/* Create Depreciation Sort Code Dialog */}
        <CreateDepreciationSortCodeDialog
            open={isCreateSortCodeDialogOpen}
            onClose={() => {
                setIsCreateSortCodeDialogOpen(false);
                setSelectedAssetGroupForSortCode(null);
                setSelectedAssetGroupAmount(null); // Reset amount when closing sort code dialog
            }}
            client={client}
            token={token}
            direction={direction}
            existingSortCodes={localSortCodeIdOptions}
            assetGroups={depreciationData?.assetGroups || []}
            onSaveSuccess={handleSortCodeSaveSuccess}
            initialSortCodeName={selectedAssetGroupForSortCode ? `${Constants.DepreciationPrefixes.SortCode}${selectedAssetGroupForSortCode.assetGroupName}` : null}
        />
    </>
    );
};

export default DepreciationJournalEntriesModal;

